import { useMemo, useState } from 'react';
import { AccessLevel, PermissionModule, accessLevel } from '@lombok/shared';
import type { InventoryBalance, InventoryListItem } from '@lombok/shared';
import { Badge, Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { SearchInput } from '@/components/SearchInput';
import { QueryState } from '@/components/QueryState';
import { useAuth } from '@/auth/AuthContext';
import { useInventory } from '@/data/hooks';
import { placeLabel } from '@/lib/names';
import { StockAdjustPanel, type StockAdjustTarget } from './StockAdjustPanel';
import { SortSelect } from '@/components/SortSelect';
import { byNumber, byText, sortItems, thenBy, type SortOption } from '@/lib/sort';

const productName = (b: InventoryListItem) => b.productName ?? b.skuName ?? b.skuId;
/** Most urgent first: sold out, backorder, low stock, then healthy (same rules as the Alerta badge). */
const alertRank = (b: InventoryListItem) =>
  b.availableQty <= 0 ? (b.allowBackorder ? 1 : 0) : b.availableQty <= b.reorderPoint ? 2 : 3;

const INVENTORY_SORT: SortOption<InventoryListItem>[] = [
  { value: 'az', label: 'Producto A–Z', compare: thenBy<InventoryListItem>(byText(productName), byText((b) => b.locationId)) },
  { value: 'za', label: 'Producto Z–A', compare: thenBy<InventoryListItem>(byText(productName, 'desc'), byText((b) => b.locationId)) },
  { value: 'available_asc', label: 'Disponible menor', compare: thenBy<InventoryListItem>(byNumber((b) => b.availableQty), byText(productName)) },
  { value: 'available_desc', label: 'Disponible mayor', compare: thenBy<InventoryListItem>(byNumber((b) => b.availableQty, 'desc'), byText(productName)) },
  { value: 'alert', label: 'Alerta primero', compare: thenBy<InventoryListItem>(byNumber(alertRank), byNumber((b) => b.availableQty), byText(productName)) },
];

/**
 * Inventario (admin). Shows EXACT stock balances — admins/warehouse see real
 * quantities (on-hand, reserved, available, incoming, reorder point). This is
 * NOT a customer-facing surface: customers only ever get the AVAILABILITY_LABELS_ES
 * labels (see ProductsListPage / AvailabilityBadge), never these numbers.
 *
 * The `lowStock` toggle maps to the backend GET /inventory query (balances at/under their
 * reorder point); without it, every balance is listed. A free-text box additionally filters
 * the loaded rows by product, SKU or location on the client. Roles with FULL inventory access
 * (admin, warehouse) can also receive stock and correct counts from here.
 */
export function InventoryListPage() {
  const [lowStock, setLowStock] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('az');
  const { session } = useAuth();
  const canAdjust = session
    ? accessLevel(session.user.role, PermissionModule.INVENTORY_EXACT) === AccessLevel.FULL
    : false;
  // `null` = panel closed; no target = receive stock for any SKU; a target = adjust that balance.
  const [adjusting, setAdjusting] = useState<{ target?: StockAdjustTarget } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useInventory({ lowStock: lowStock || undefined });

  const items = query.data?.items ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = q
      ? items.filter((b) =>
          [b.productName, b.skuName, b.skuCode, b.skuId, b.locationId].some((v) => v?.toLowerCase().includes(q)),
        )
      : items;
    return sortItems(matches, INVENTORY_SORT, sort);
  }, [items, search, sort]);

  const knownLocations = useMemo(() => [...new Set(items.map((b) => b.locationId))].sort(), [items]);
  const adjustTarget = adjusting?.target;
  const openAdjust = (target?: StockAdjustTarget) => {
    setNotice(null);
    setAdjusting({ target });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Inventario"
        description="Existencias exactas por ubicación (uso interno)."
        actions={
          canAdjust ? (
            <Button
              size="sm"
              onClick={() => openAdjust()}
            >
              Ingresar stock
            </Button>
          ) : undefined
        }
      />

      {notice ? (
        <p role="status" className="rounded-md border border-success-soft bg-success-soft p-3 text-sm text-text-primary">
          {notice}
        </p>
      ) : null}

      {adjusting ? (
        <StockAdjustPanel
          // A new key per target resets the form when switching between rows.
          key={adjustTarget ? `${adjustTarget.locationId}:${adjustTarget.skuId}` : 'new'}
          target={adjustTarget}
          knownLocations={knownLocations}
          onCancel={() => setAdjusting(null)}
          onDone={(message) => {
            setAdjusting(null);
            setNotice(message);
          }}
        />
      ) : null}

      <Card className="overflow-hidden">
        {/* Filters */}
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:gap-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Filtrar por producto, SKU o ubicación…"
          />
          <label className="inline-flex select-none items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={lowStock}
              onChange={(e) => setLowStock(e.target.checked)}
              className="h-5 w-5 rounded border-border-strong text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            Solo bajo stock
          </label>
          <SortSelect options={INVENTORY_SORT} value={sort} onChange={setSort} />
        </div>

        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={filtered.length === 0}
          loadingLabel="Cargando inventario…"
          loadingVariant="table"
          skeletonCols={5}
          emptyTitle={
            search || lowStock ? 'Sin coincidencias' : 'No hay existencias'
          }
          emptyDescription={
            search || lowStock
              ? 'Ajusta los filtros para ver más resultados.'
              : 'Aún no se han registrado balances de inventario.'
          }
          emptyAction={
            search || lowStock ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setLowStock(false);
                }}
              >
                Limpiar filtros
              </Button>
            ) : undefined
          }
        >
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Producto</th>
                  <th className="px-4 py-3 font-medium">Ubicación</th>
                  <th className="px-4 py-3 text-right font-medium">En mano</th>
                  <th className="px-4 py-3 text-right font-medium">Reservado</th>
                  <th className="px-4 py-3 text-right font-medium">Disponible</th>
                  <th className="px-4 py-3 text-right font-medium">En camino</th>
                  <th className="px-4 py-3 text-right font-medium">Punto de reorden</th>
                  <th className="px-4 py-3 font-medium">Alerta</th>
                  {canAdjust ? <th className="px-4 py-3 text-right font-medium">Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={`${b.locationId}:${b.skuId}`}
                    className="border-b border-border last:border-0 hover:bg-surface-muted"
                  >
                    <td className="px-4 py-3">
                      <ProductCell item={b} />
                    </td>
                    <td className="px-4 py-3 text-text-muted">{placeLabel(b.locationId)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{b.onHandQty}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-muted">
                      {b.reservedQty}
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums text-text-primary">
                      {b.availableQty}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-muted">
                      {b.incomingQty}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-text-muted">
                      {b.reorderPoint}
                    </td>
                    <td className="px-4 py-3">
                      <StockBadge balance={b} />
                    </td>
                    {canAdjust ? (
                      <td className="px-4 py-3 text-right">
                        <AdjustButton balance={b} onAdjust={openAdjust} />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {filtered.map((b) => (
              <li key={`${b.locationId}:${b.skuId}`} className="flex flex-col gap-2 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <ProductCell item={b} />
                    <p className="text-xs text-text-muted">{placeLabel(b.locationId)}</p>
                  </div>
                  <StockBadge balance={b} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <Metric label="En mano" value={b.onHandQty} />
                  <Metric label="Reservado" value={b.reservedQty} />
                  <Metric label="Disponible" value={b.availableQty} strong />
                  <Metric label="En camino" value={b.incomingQty} />
                  <Metric label="Reorden" value={b.reorderPoint} />
                </div>
                {canAdjust ? (
                  <AdjustButton balance={b} onAdjust={openAdjust} />
                ) : null}
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

/** What a balance counts, in words: product on top, presentation and code underneath. */
function ProductCell({ item }: { item: InventoryListItem }) {
  const title = item.productName ?? item.skuName ?? item.skuId;
  const detail = [item.skuName === title ? undefined : item.skuName, item.skuCode].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-col">
      <span className="font-medium text-text-primary">{title}</span>
      {detail ? <span className="text-xs text-text-muted">{detail}</span> : null}
    </div>
  );
}

function AdjustButton({
  balance: b,
  onAdjust,
}: {
  balance: InventoryListItem;
  onAdjust: (target: StockAdjustTarget) => void;
}) {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() =>
        onAdjust({
          skuId: b.skuId,
          locationId: b.locationId,
          productName: b.productName,
          skuLabel: [b.skuName, b.skuCode].filter(Boolean).join(' · ') || undefined,
        })
      }
    >
      Ajustar
    </Button>
  );
}

function Metric({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className="rounded-md bg-surface-muted p-2">
      <p className="text-xs text-text-muted">{label}</p>
      <p className={`tabular-nums ${strong ? 'font-semibold text-text-primary' : 'text-text-primary'}`}>
        {value}
      </p>
    </div>
  );
}

/** Stock health badge derived from exact quantities (internal view only). */
function StockBadge({ balance: b }: { balance: InventoryBalance }) {
  if (b.availableQty <= 0) {
    return b.allowBackorder ? (
      <Badge tone="warning">Backorder</Badge>
    ) : (
      <Badge tone="danger">Agotado</Badge>
    );
  }
  if (b.availableQty <= b.reorderPoint) {
    return <Badge tone="warning">Bajo stock</Badge>;
  }
  return <Badge tone="success">OK</Badge>;
}
